<template>
  <BaseWrapper :loading="loading">
    <div class="space-y-4">
      <ShipmentOrderDetailCard :label="label" :type="type" :order="order" />

      <BaseCard
        title="Informasi Barang Keluar"
        description="Daftar barang yang akan dikirim"
        v-if="!packed || originOfficeType === 'stockist'"
      >
        <Datatable
          compact
          :columns="[
            {
              id: 'kode barang',
              name: 'Kode Barang',
            },
            {
              id: 'nama barang',
              name: 'Nama Barang',
            },
            {
              id: 'jumlah',
              name: 'Jumlah',
              theadClass: 'text-right',
            },
            {
              id: 'harga',
              name: 'Harga',
              theadClass: 'text-right',
            },
            {
              id: 'harga total',
              name: 'Harga Total',
              theadClass: 'text-right',
            },
          ]"
        >
          <template #tbody="{ classes }">
            <tr
              v-for="product in products"
              :key="`${product.type}-${product.id}`"
              :class="classes.tr"
            >
              <td :class="classes.td">
                <p class="font-bold text-gray-900">
                  {{ product.attributes.product_code }}
                  <span v-if="product.attributes.product_type === 'free'"
                    >(PROMO)</span
                  >
                  <span v-if="type === 'order' && product.type === 'order-detail-bonuses'"
                    >(FREE)</span
                  >
                </p>
              </td>
              <td :class="classes.td">{{ product.attributes.product_name }}</td>
              <td :class="[classes.td, 'text-right']">
                {{
                  getProductQty(product) | toCurrency
                }}
              </td>
              <td :class="[classes.td, 'text-right']">
                {{
                  (product.type === 'order-details'
                    ? product.attributes.product_price
                    : 0) | toCurrency
                }}
              </td>
              <td :class="[classes.td, 'text-right']">
                {{
                  (product.type === 'order-details'
                    ? product.attributes.total_price
                    : 0) | toCurrency
                }}
              </td>
            </tr>
          </template>
        </Datatable>
      </BaseCard>

      <template v-else>
        <BaseCard
          title="Informasi Koli"
          description="Daftar koli yang akan dikirim"
        >
          <Datatable
            compact
            :columns="[
              {
                id: 'kode koli',
                name: 'Kode Koli',
              },
              {
                id: 'nama koli',
                name: 'Nama Koli',
              },
            ]"
          >
            <template #tbody="{ classes }">
              <tr
                v-for="packet in stockMovementPackets"
                :key="packet.id"
                :class="[classes.tr, 'cursor-pointer hover:bg-green-100']"
                @click="onClickDetailKoli(packet)"
              >
                <td :class="classes.td">
                  <p class="font-bold text-gray-900">
                    {{ packet.attributes.code }}
                  </p>
                </td>
                <td :class="classes.td">{{ packet.attributes.name }}</td>
              </tr>
            </template>
          </Datatable>
        </BaseCard>

        <BaseCard
          title="Barang Susulan"
          description="Daftar barang yang belum dikirim"
          v-if="productsNotPacked.length"
        >
        <Datatable
          compact
          :columns="[
            {
              id: 'kode barang',
              name: 'Kode Barang',
            },
            {
              id: 'nama barang',
              name: 'Nama Barang',
            },
            {
              id: 'jumlah',
              name: 'Jumlah',
              theadClass: 'text-right',
            },
          ]"
        >
          <template #tbody="{ classes }">
            <tr
              v-for="product in productsNotPacked"
              :key="`${product.type}-${product.id}`"
              :class="classes.tr"
            >
              <td :class="classes.td">
                <p class="font-bold text-gray-900">
                  {{ product.attributes.product_code }}
                  <span v-if="product.attributes.product_type === 'free'"
                    >(PROMO)</span
                  >
                  <span v-if="product.type === 'order-detail-bonuses'"
                    >(FREE)</span
                  >
                </p>
              </td>
              <td :class="classes.td">{{ product.attributes.product_name }}</td>
              <td :class="[classes.td, 'text-right']">
                {{
                  getFollowUpQty(product) | toCurrency
                }}
              </td>
            </tr>
          </template>
        </Datatable>
        </BaseCard>
      </template>

      <ShipmentOrderFeeCard v-if="withFee && order.data.attributes.order_shipment === 'delivery'" :order="order" />

      <div class="flex justify-between">
        <div class="flex gap-2">
          <template v-if="canCancel && !reported">
            <BaseButton v-if="type === 'order' && !packed" color="white" @click="cancelOrderVisible = true">
              <Icon icon="heroicons:x-mark" class="h-4 w-4" />
              Batalkan Faktur Penjualan
            </BaseButton>
            <BaseButton
              v-if="packed && !readyToShip"
              color="white"
              @click="deleteVisible = true"
            >
              <Icon icon="heroicons:x-mark" class="h-4 w-4" />
              Batalkan Faktur Kemasan
            </BaseButton>
          </template>
          <BaseButton
            type="button"
            v-if="toMember"
            @click="onPrintCashbill"
          >
            <Icon icon="heroicons:printer-solid" class="h-4 w-4" />
            Cetak Cashbill
          </BaseButton>
          <BaseButton
            type="button"
            v-if="!printSppbEnabled && canPrint"
            @click="onRePrintSppb"
          >
            <Icon icon="heroicons:printer-solid" class="h-4 w-4" />
            Cetak SPPB
          </BaseButton>
        </div>

        <div class="flex items-center gap-2">
          <div v-if="packed" class="flex gap-2">
            <BaseButton v-if="originOfficeType !== 'stockist'" @click="onPrintReceipt">
              <Icon icon="heroicons:printer-solid" class="h-4 w-4" />
              Cetak Surat Tanda Terima
            </BaseButton>
            <template v-if="!readyToShip">
              <BaseButton v-if="originOfficeType === 'stockist'" @click="validateVisible = true">
                <Icon icon="heroicons:archive-box-solid" class="h-4 w-4" />
                Proses Ke Pengiriman
              </BaseButton>
              <BaseButton v-else @click="validateVisible = true">
                <Icon icon="heroicons:truck-solid" class="h-4 w-4" />
                {{ stockMovement.attributes.shipment_type === 'pickup' ? 'Siap Diambil!' : 'Siap Kirim!' }}
              </BaseButton>
            </template>
          </div>
          <div v-else-if="canPack">
            <template v-if="printSppbEnabled">
              <BaseButton
                v-if="canPrint"
                @click="disableOrderInvoiceVisible = true"
              >
                <Icon icon="heroicons:printer-solid" class="h-4 w-4" />
                Cetak SPPB
              </BaseButton>
            </template>
            <template v-else>
              <BaseButton v-if="originOfficeType === 'stockist'" @click="onCreateMemberShipment">
                <Icon icon="heroicons:archive-box-solid" class="h-4 w-4" />
                Proses Ke Pengiriman
              </BaseButton>
              <template v-else>
                <BaseButton
                  v-if="!prepared"
                  @click="preparingName = true"
                >
                  <Icon icon="heroicons:user-solid" class="h-4 w-4" />
                  Input Petugas Gudang
                </BaseButton>
                <BaseButton
                  v-else
                  :to="pengemasanPath ? { name: pengemasanPath, params: { id: order.data.id } } : `${indexPath}/${order.data.id}/pengemasan`"
                >
                  <Icon icon="heroicons:archive-box-solid" class="h-4 w-4" />
                  Rincian Koli
                </BaseButton>
              </template>
            </template>
          </div>

          <BaseButton color="white" :to="indexPath">
            <Icon icon="heroicons:arrow-uturn-left" class="h-4 w-4" />
            Kembali
          </BaseButton>
        </div>
      </div>
    </div>

    <CancelOrderShipmentConfirmation
      :visible="cancelOrderVisible"
      :order-id="order.data.id"
      :has-stock-movement="packed"
      @close="cancelOrderVisible = !cancelOrderVisible"
      @confirmed="$router.push(indexPath)"
    />
    <DisableOrderInvoiceConfirmation
      :visible="disableOrderInvoiceVisible"
      :order-id="order.data.id"
      :with-loading="false"
      :type="type"
      @submit="loadingDisableInvoice = true"
      @close="disableOrderInvoiceVisible = !disableOrderInvoiceVisible"
      @confirmed="onDisabledInvoice"
    />
    <InputOrderPreparedByNameConfirmation
      :visible="preparingName"
      :order-id="order.data.id"
      :with-loading="false"
      @submit="loadingPreparingName = true"
      @close="preparingName = !preparingName"
      @confirmed="onNamePrepared"
    />
    <DeleteShipmentConfirmation
      :visible="deleteVisible"
      :stock-movement-id="stockMovement ? stockMovement.id : null"
      @close="deleteVisible = false"
      @confirmed="onDeleted"
    />
    <ValidateShipmentConfirmation :order="order" :stock-movement="stockMovement" :visible="validateVisible" @close="validateVisible = false" @validated="$router.push(indexPath)" />
    <ViewPacketModal
      :with-complain-form="false"
      :packet="detailKoliModal.packet"
      :visible="detailKoliModal.visible"
      with-batches
      @close="detailKoliModal.visible = false"
    />

    <VueHtml2pdf
      :show-layout="false"
      :float-layout="true"
      :preview-modal="true"
      :enable-download="true"
      :paginate-elements-by-height="5000"
      :filename="filename"
      :pdf-quality="2"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="210mm"
      ref="html2Pdf"
    >
      <PdfContent :label="label" slot="pdf-content" :order="order" />
    </VueHtml2pdf>
    <VueHtml2pdf
      :show-layout="false"
      :float-layout="true"
      :preview-modal="true"
      :enable-download="true"
      :paginate-elements-by-height="5000"
      :filename="filenameSttb"
      :pdf-quality="2"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="210mm"
      ref="printStockMovementEl"
    >
      <LayoutPrintStockMovementReceipt
        slot="pdf-content"
        display-per-product
        :details="movementDetails.data"
        :stock-movement="stockMovement"
        :order="{
          id: order.data.id,
          code: order.data.attributes.origin_code,
          date: order.data.attributes.orderedAt || order.data.attributes.createdAt,
          destinationOfficeCode: destinationOffice
            ? destinationOffice.attributes.code
            : '-',
          destinationOfficeName: destinationOffice
            ? destinationOffice.attributes.name
            : '-',
          destinationOfficeAddress,
          orderAddress,
          shipmentType: order.data.attributes.order_shipment,
          type: orderType,
          details: products.map((detail) => ({
            product_code: detail.attributes.product_code,
            product_qty: detail.attributes.product_qty,
            product_name: detail.attributes.product_name,
          })),
        }"
      />
    </VueHtml2pdf>

    <VueHtml2pdf
      v-if="toMember"
      :show-layout="false"
      :float-layout="true"
      :preview-modal="true"
      :enable-download="true"
      :paginate-elements-by-height="5000"
      :filename="filenameCashbill"
      :pdf-quality="2"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="210mm"
      ref="cashbillPdf"
    >
      <PrintOrderMemberPdf
        slot="pdf-content"
        :order="order.data"
        :origin-office="originOffice"
        :destination-office="destinationOffice"
        :order-details="getIncludedByType(order, 'order-details')"
        :order-detail-bonuses="getIncludedByType(order, 'order-detail-bonusess')"
      />
    </VueHtml2pdf>
  </BaseWrapper>
</template>

<script>
import ViewPacketModal from '@/components/stock-movement/view-packet-modal.vue';
import ShipmentOrderDetailCard from '@/components/shipment/shipment-order-detail-card.vue';
import ShipmentOrderFeeCard from '@/components/shipment/shipment-order-fee-card.vue';
import CancelOrderShipmentConfirmation from '@/components/order/cancel-order-shipment-confirmation.vue';
import DisableOrderInvoiceConfirmation from '@/components/order/disable-order-invoice-confirmation.vue';
import DeleteShipmentConfirmation from '@/components/stock-movement/delete-shipment-confirmation.vue';
import InputOrderPreparedByNameConfirmation from '@/components/order/input-order-prepared-by-name-confirmation.vue';
import VueHtml2pdf from 'vue-html2pdf';
import PdfContent from '@/layouts/LayoutPrintA4.vue';
import { requestMixin } from '@/mixins/request/request';
import LayoutPrintStockMovementReceipt from '@/layouts/LayoutPrintStockMovementReceipt.vue';
import ValidateShipmentConfirmation from '@/components/stock-movement/validate-shipment-confirmation.vue';
import { mapActions, mapGetters } from 'vuex';
import dayjs from 'dayjs';
import PrintOrderMemberPdf from './print-order-member-pdf.vue';

export default {
  mixins: [requestMixin],
  components: {
    ShipmentOrderDetailCard,
    ShipmentOrderFeeCard,
    CancelOrderShipmentConfirmation,
    DisableOrderInvoiceConfirmation,
    DeleteShipmentConfirmation,
    VueHtml2pdf,
    PdfContent,
    LayoutPrintStockMovementReceipt,
    ValidateShipmentConfirmation,
    InputOrderPreparedByNameConfirmation,
    ViewPacketModal,
    PrintOrderMemberPdf
  },
  props: {
    indexPath: null,
    withFee: {
      type: Boolean,
      default: true
    },
    pengemasanPath: null,
    cancelable: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'order'
    },
    label: String,
    movementType: {
      type: String,
      default: 'main'
    },
  },
  data() {
    return {
      accReason: null,
      detailKoliModal: {
        packet: {},
        visible: false
      },
      loadingOrder: false,
      loadingDisableInvoice: false,
      loadingPreparingName: false,
      loadingStockMovement: false,
      loadingSend: false,
      cancelOrderVisible: false,
      disableOrderInvoiceVisible: false,
      deleteVisible: false,
      validateVisible: false,
      preparingName: false,
      order: {
        data: {
          attributes: {},
          relationships: {
            'destination-office': {
              data: null,
            },
            village: {
              data: null,
            },
            district: {
              data: null,
            },
            city: {
              data: null,
            },
            province: {
              data: null,
            },
          },
        },
        included: [],
      },
      stockMovement: null,
      stockMovementPackets: [],
      movementDetails: {
        data: []
      }
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
      checkHasPermission: 'auth/checkHasPermission',
    }),
    filename() {
      const datetime = dayjs(this.order.data.attributes.createdAt).format('YYYYMMDD')
      const type = 'sppb'
      const code = `${this.order.data.attributes.origin_code}_${this.order.data.attributes.destination_code}`
      const user = this.me.office_code

      return [datetime, type, code, user].join('-')
    },
    filenameSttb() {
      const datetime = dayjs(this.order.data.attributes.createdAt).format('YYYYMMDD')
      const type = 'sttb'
      const code = `${this.order.data.attributes.origin_code}_${this.order.data.attributes.destination_code}`
      const user = this.me.office_code

      return [datetime, type, code, user].join('-')
    },
    filenameCashbill() {
      const datetime = dayjs(this.order.data.attributes.createdAt).format('YYYYMMDD')
      const type = 'cashbill'
      const code = `${this.order.data.attributes.origin_code}_${this.order.data.attributes.destination_code}`
      const user = this.me.office_code

      return [datetime, type, code, user].join('-')
    },
    canCancel() {
      return this.me.permissions.includes('orders.cancel_warehouse') && this.cancelable
    },
    originOffice() {
      const originOffice =
        this.order.data.relationships['origin-office'].data;

      if (!originOffice) {
        return null;
      }

      return this.getSingleIncluded(this.order, originOffice.id);
    },
    destinationOffice() {
      const destinationOffice =
        this.order.data.relationships['destination-office'].data;

      if (!destinationOffice) {
        return null;
      }

      return this.getSingleIncluded(this.order, destinationOffice.id);
    },
    toMember() {
      if (!this.destinationOffice) {
        return false
      }

      return this.destinationOffice.attributes.office_type === 'member'
    },
    destinationOfficeAddress() {
      if (!this.destinationOffice) {
        return null
      }

      return `${this.destinationOffice.attributes.name}, ${this.destinationOffice.attributes.phone ?? ''}, ${this.destinationOffice.attributes.full_address}`
    },
    orderAddress() {
      return this.order.data.attributes.complete_shipment_address
    },
    orderType() {
      if (this.type === 'reimbursement') {
        return 'Reimbursement'
      }

      if (this.type === 'free_reimbursement') {
        return 'Susulan Promo'
      }

      if (this.order.data.attributes.is_transfer_promo || this.order.data.attributes.is_transfer_promo_regular) {
        return 'Kirim Promo'
      }

      if (!this.destinationOffice) {
        return 'Penjualan'
      }

      if (['stockist_center', 'kantor_pelayanan'].includes(this.destinationOffice.attributes.office_type)) {
        return 'Restock'
      }

      return 'Penjualan'
    },
    packed() {
      return !!this.stockMovement && this.stockMovement.attributes.is_packed;
    },
    canPack() {
      if (this.type !== 'reimbursement' && this.type !== 'free_reimbursement') {
        return this.order.data.attributes.order_status !== 'Dikirim'
      }

      if (this.type === 'reimbursement') {
        if (this.movementType === 'main') {
          return !['ada_susulan', 'susulan_siap_dikemas', 'susulan_diproses_pengiriman', 'susulan_dikirim', 'selesai'].includes(this.order.data.attributes.reimbursement_status)
        }
      }

      return true
    },
    readyToShip() {
      return !!this.stockMovement && this.stockMovement.attributes.is_valid_for_shipment
    },
    printSppbEnabled() {
      if (this.type === 'reimbursement') {
        return !this.order.data.attributes.reimbursement_status
      }

      if (this.type === 'free_reimbursement') {
        return !this.order.data.attributes.free_reimbursement_status
      }

      return this.order.data.attributes.print_invoice_enabled
    },
    prepared() {
      return this.order.data.attributes.prepared
    },
    reported() {
      return this.order.data.attributes.is_bonus_reported
    },
    loading() {
      return (
        this.loadingOrder ||
        this.loadingDisableInvoice ||
        this.loadingStockMovement ||
        this.loadingPreparingName ||
        this.loadingSend
      );
    },
    products() {
      if (this.type === 'reimbursement') {
        return this.getIncludedByType(this.order, 'order-detail-bonuses')
          .filter(bonus => bonus.attributes.reimbursement_shipped_qty < bonus.attributes.regular_stock_qty)
      }

      if (this.type === 'free_reimbursement') {
        return this.getIncludedByType(this.order, 'order-detail-bonuses')
          .filter(bonus => bonus.attributes.free_follow_up_shipped_qty < bonus.attributes.free_follow_up_qty)
      }

      return [
        ...this.getIncludedByType(this.order, 'order-details'),
        ...this.getIncludedByType(this.order, 'order-detail-bonuses'),
      ].filter(item => {
        if (item.type === 'order-detail-bonuses') {
          return item.attributes.bonus_shipped_qty < item.attributes.bonus_qty
        }

        return item.attributes.product_shipped_qty < item.attributes.product_qty
      });
    },
    productsNotPacked() {
      if (this.type === 'reimbursement') {
        return this.products.filter(bonus => bonus.attributes.reimbursement_not_packed_qty > 0)
      }

      if (this.type === 'free_reimbursement') {
        return this.products.filter(bonus => bonus.attributes.free_follow_up_not_packed_qty > 0)
      }

      return this.products.filter(item => {
        if (item.type === 'order-detail-bonuses') {
          return item.attributes.bonus_not_packed_qty > 0
        }

        return item.attributes.product_not_packed > 0
      })
    },
    originOfficeType() {
      return this.me.office_type
    },
    canPrint() {
      if (this.originOfficeType !== 'pusat') {
        return true
      }

      return this.checkHasPermission('orders.read_persiapan')
    }
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    getProductQty(product) {
      if (this.type === 'reimbursement') {
        return product.attributes.regular_stock_qty - product.attributes.reimbursement_shipped_qty
      }

      if (this.type === 'free_reimbursement') {
        return product.attributes.free_follow_up_qty - product.attributes.free_follow_up_shipped_qty
      }

      if (product.type === 'order-detail-bonuses') {
        return product.attributes.bonus_qty - product.attributes.bonus_shipped_qty
      }

      return product.attributes.product_qty - product.attributes.product_shipped_qty
    },
    getFollowUpQty(product) {
      if (this.type === 'reimbursement') {
        return product.attributes.reimbursement_not_packed_qty
      }

      if (this.type === 'free_reimbursement') {
        return product.attributes.free_follow_up_not_packed_qty
      }

      if (product.type === 'order-detail-bonuses'){
        return product.attributes.bonus_not_packed_qty
      }

      return product.attributes.product_not_packed
    },
    async loadOrder() {
      this.loadingOrder = true;

      const include = [
        'destination-office',
        'village',
        'district',
        'city',
        'province',
        ...(this.type === 'reimbursement' || this.type === 'free_reimbursement' ? [] : ['order-details']),
        'order-detail-bonuses',
        'origin-office',
        'destination-warehouse'
      ].join(',')

      const [res, err] = await this.request(
        `/api/v1/orders/${this.$route.params.id}`,
        {
          params: {
            include,
            'fields[orders]':
              'is_bonus_reported,receipt_name,receipt_phone,complete_shipment_address,prepared,warehouse_status,destination_code,sppb_print_count,origin_code,reference_code,createdAt,orderedAt,destination-office,order_shipment,shipment_address,village,district,city,province,order-details,order-detail-bonuses,print_invoice_enabled,origin-office,area_name,area_code,notes,destination-warehouse,grand_total_price,shipping_fee,plastic_qty,reimbursement_status,reimbursement_status_label,free_reimbursement_status,free_reimbursement_status_label,order_status,is_transfer_promo,is_transfer_promo_regular',
            'fields[offices]':
              'code,name,full_address,phone,office_type,address_details,kta',
            'fields[villages]': 'name',
            'fields[districts]': 'name',
            'fields[cities]': 'name',
            'fields[provinces]': 'name',
            'fields[order-details]':
              'product_type,product_code,product_name,product_qty,product_price,total_price,bonus_value,product_shipped_qty,product_not_packed,product_remaining_qty',
            'fields[order-detail-bonuses]':
              'product_code,product_name,bonus_qty,bonus_shipped_qty,bonus_remaining_qty,bonus_not_packed_qty,regular_stock_qty,reimbursement_not_packed_qty,free_follow_up_qty,free_follow_up_not_packed_qty,free_follow_up_shipped_qty,reimbursement_shipped_qty',
            'fields[warehouses]': 'code',
          },
        }
      );

      if (!err) {
        this.order = res;

        localStorage.removeItem('print_sppb_page')
      }

      this.loadingOrder = false;
    },
    async loadStockMovement() {
      this.loadingStockMovement = true;

      const [res, err] = await this.request(
        `/api/v1/orders/${this.$route.params.id}/stock-movements`,
        {
          params: {
            sort: 'id',
            'page[size]': 1,
            'fields[stock-movements]': 'shipment_type,expedition_name,code,is_valid_for_shipment,is_packed,packets',
            'include': 'packets',
            'fields[packets]': 'code,name',
            'filter[is_shipped]': false,
            'filter[for_reimbursement]': this.type === 'reimbursement',
            'filter[for_free_reimbursement]': this.type === 'free_reimbursement'
          },
        }
      );

      if (!err) {
        this.stockMovement = res.data[0];
        this.stockMovementPackets = this.getIncludedByType(res, 'packets', this.stockMovement)

        if (this.stockMovement) {
          this.loadMovementDetails()
        }
      }

      this.loadingStockMovement = false;
    },
    async loadMovementDetails() {
      const [res, err] = await this.request(`/api/v1/stock-movements/${this.stockMovement.id}/movement-details`, {
        params: {
          'fields[movement-details]': 'packet_code,product_code,product_name,product_type,shipped_product_qty,product_weight'
        }
      })

      if (!err) {
        this.movementDetails = res
      }
    },
    loadData() {
      this.loadOrder();
      this.loadStockMovement();
    },
    async onDisabledInvoice() {
      this.disableOrderInvoiceVisible = false;

      await this.loadOrder();

      this.loadingDisableInvoice = false;

      setTimeout(() => {
        this.$refs.html2Pdf.generatePdf();
      }, 0);
    },
    async onNamePrepared() {
      this.preparingName = false;

      await this.loadOrder();

      this.loadingPreparingName = false;
    },
    async onRePrintSppb() {
      this.loadingDisableInvoice = true;

      const [, err] = await this.request(
        `/api/v1/orders/${this.order.data.id}/-actions/print-sppb`,
        {
          method: 'patch',
        }
      );

      if (err) {
        this.createAlert({
          data: this.getRequestErrorMessage(err),
          status: 'error',
        });
      } else {
        await this.loadOrder();

        setTimeout(() => {
          this.$refs.html2Pdf.generatePdf();
        }, 0);
      }

      this.loadingDisableInvoice = false;
    },
    async onPrintCashbill() {
      setTimeout(() => {
        this.$refs.cashbillPdf.generatePdf();
      }, 0);
    },
    onDeleted() {
      this.deleteVisible = false;

      this.loadData();
    },
    onPrintReceipt() {
      setTimeout(() => this.$refs.printStockMovementEl.generatePdf(), 0);
    },
    onClickDetailKoli(packet) {
      this.detailKoliModal.packet = packet;
      this.detailKoliModal.visible = true;
    },
    async onCreateMemberShipment() {
      this.loadingSend = true;

      const [, err] = await this.request(`/api/v1/orders/${this.order.data.id}/-actions/create-shipment`, {
        method: 'post'
      });

      if (!err) {
        this.$router.push(this.indexPath);
      }

      this.loadingSend = false;
    }
  },
  created() {
    this.loadData();
  },
};
</script>
